import { ArrowUpRight, ImageOff } from 'lucide-react'
import Link from 'next/link'
import { useHubAnalytics } from '../HubAnalyticsContext'
import type {
  HubReportGridSection,
  HubReportRef,
  HubSectionComponentProps,
} from './types'

/* color-token-exception: decorative gradients for cards without imagery.
 * Mirrors SeoReportRelatedReports — kit-internal data, not LLM-controlled. */
const FALLBACK_GRADIENTS = [
  'linear-gradient(135deg, #f5c6aa 0%, #e8a87c 100%)',
  'linear-gradient(135deg, #c3a5d9 0%, #9b7fc4 100%)',
  'linear-gradient(135deg, #7db4d8 0%, #5a8fb5 100%)',
  'linear-gradient(135deg, #a8d8c5 0%, #7bc4a5 100%)',
  'linear-gradient(135deg, #f0b6c1 0%, #d98ea0 100%)',
] as const

function ReportCard({
  report,
  index,
  overrideTitle,
  blurb,
}: {
  report: HubReportRef
  index: number
  overrideTitle?: string
  blurb?: string
}) {
  const hubAnalytics = useHubAnalytics()
  const fallback = FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length]
  const displayTitle = overrideTitle ?? report.title
  const description = blurb ?? report.excerpt

  return (
    <Link
      href={report.href}
      className='group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-white text-left transition-[border-color,box-shadow] duration-300 hover:border-foreground-secondary hover:shadow-sm'
      onClick={() =>
        hubAnalytics?.onReportClick({
          reportId: report.reportId,
          reportSlug: report.slug,
          reportTitle: displayTitle,
          position: index,
        })
      }
    >
      <div className='relative aspect-[4/3] w-full overflow-hidden bg-ds-bg-secondary'>
        {report.imageUrl ? (
          <img
            src={report.imageUrl}
            alt={displayTitle}
            className='h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105'
            loading='lazy'
          />
        ) : (
          <div
            className='flex h-full w-full items-center justify-center transition-transform duration-500 ease-out group-hover:scale-105'
            style={{ background: fallback }}
          >
            <ImageOff className='size-8 text-white/75' strokeWidth={1.5} />
          </div>
        )}
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-3 p-4'>
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <p className='text-balance text-base font-semibold leading-6 text-foreground'>
            {displayTitle}
          </p>
          {description && (
            <p className='line-clamp-3 text-sm leading-5 text-foreground-secondary'>
              {description}
            </p>
          )}
        </div>
        <span className='inline-flex items-center gap-1 text-sm font-semibold text-info-700'>
          Read report
          <ArrowUpRight
            className='size-4 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5'
            aria-hidden
          />
        </span>
      </div>
    </Link>
  )
}

export function HubReportGrid({
  section,
  context,
}: HubSectionComponentProps<HubReportGridSection>) {
  const resolved: Array<{
    item: (typeof section.items)[number]
    report: HubReportRef
  }> = []
  for (const item of section.items) {
    const report = context.reportsById[item.reportId]
    if (!report) continue
    resolved.push({ item, report })
  }

  if (resolved.length === 0) return null

  return (
    <section id={section.id} className='flex flex-col gap-5'>
      {section.title && (
        <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
          {section.title}
        </h2>
      )}
      {section.dek && (
        <p className='text-base leading-6 text-foreground-secondary'>
          {section.dek}
        </p>
      )}

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        {resolved.map(({ item, report }, i) => (
          <ReportCard
            key={report.reportId}
            report={report}
            index={i}
            overrideTitle={item.title}
            blurb={item.blurb}
          />
        ))}
      </div>
    </section>
  )
}
